import React from "react"
// MUI
import {Box, Typography, Button, IconButton} from "@mui/material"
import {alpha} from "@mui/material/styles"
import {NavigateNextRounded, AutoAwesomeRounded, MenuRounded, ScienceRounded, MenuBookRounded} from "@mui/icons-material"
// Translation
import {useTranslation} from "react-i18next"
// Tokens
import {packagingTokens as tk} from "@/theme/packagingTokens"
// Utils
import {isLocalDev} from "@/utils"
import {parentOrigin} from "@/utils/parentOrigin"

// Keep in sync with PackagingLayout's mobile breakpoint.
const MOBILE = "md"

type Crumb = {
    label: string
    href?: string // absolute parent-dashboard URL
    onClick?: () => void // in-app navigation; omit both for the current (last) crumb
}

type TopbarProps = {
    // The section-specific trailing crumb (already translated). Omit when the
    // packaging dashboard itself is the current view — "Packaging" then closes
    // the trail: Unitag Console › Applications › Packaging [› section].
    currentCrumb?: string
    // Navigates back to the packaging dashboard section (the "Packaging" crumb).
    onPackagingHome?: () => void
    docLink?: string
    onStartTour?: () => void
    onMenuClick?: () => void
    // Local-dev only (isLocalDev): re-opens the first-visit stepper preview.
    onDevFirstVisit?: () => void
}

// Navigate the *parent* window (the app runs inside an iframe).
function goToParent(href: string) {
    const a = document.createElement("a")
    a.href = href
    a.target = "_parent"
    a.click()
}

// Follows the dashboard's canonical breadcrumb (claude-design.md §3.6 /
// AppBreadcrumbs): 12.5px, muted links → primary on hover, no underline,
// current crumb textSecondary/500. Quiet — it frames, it doesn't shout.
function BreadcrumbLink({crumb}: {crumb: Crumb}) {
    if (!crumb.href && !crumb.onClick) {
        return (
            <Typography
                component={"span"}
                sx={{fontSize: "12.5px", fontWeight: 500, color: tk.color.textSecondary}}
            >
                {crumb.label}
            </Typography>
        )
    }
    return (
        <Box
            component={"a"}
            onClick={() => (crumb.onClick ? crumb.onClick() : goToParent(crumb.href!))}
            sx={(theme) => ({
                fontSize: "12.5px",
                color: tk.color.textTertiary,
                textDecoration: "none",
                cursor: "pointer",
                transition: "color .15s ease",
                "&:hover": {color: theme.palette.primary.main},
            })}
        >
            {crumb.label}
        </Box>
    )
}

export default function PackagingTopbar(props: TopbarProps) {
    const {currentCrumb, onPackagingHome, docLink, onStartTour, onMenuClick, onDevFirstVisit} = props
    const {t} = useTranslation(["common"])

    // "Packaging" stands for the packaging dashboard: it closes the trail when
    // that's the current view, and links back to it from any other section.
    const crumbs: Crumb[] = [
        {label: t("unitag_console"), href: parentOrigin},
        {label: t("unitag_apps"), href: `${parentOrigin}/applications`},
        {label: "Packaging", onClick: currentCrumb ? onPackagingHome : undefined},
        ...(currentCrumb ? [{label: currentCrumb}] : []),
    ]

    return (
        <Box
            component={"header"}
            sx={{
                height: tk.layout.headerHeight,
                px: {xs: 1.5, md: 4},
                gap: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: tk.color.surface,
                borderBottom: `1px solid ${tk.color.border}`,
            }}
        >
            {/* Left: hamburger (mobile) + breadcrumb trail */}
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75, minWidth: 0}}>
                {onMenuClick && (
                    <IconButton
                        onClick={onMenuClick}
                        aria-label={"menu"}
                        sx={{display: {xs: "inline-flex", [MOBILE]: "none"}, mr: 0.5, color: tk.color.textSecondary}}
                    >
                        <MenuRounded />
                    </IconButton>
                )}
                <Box sx={{display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, overflow: "hidden"}}>
                    {crumbs.map((crumb, i) => {
                        const isLast = i === crumbs.length - 1
                        return (
                            <React.Fragment key={i}>
                                {i > 0 && (
                                    <NavigateNextRounded
                                        sx={{
                                            fontSize: 16,
                                            color: tk.color.textTertiary,
                                            // Hide leading separators on mobile (only the current crumb shows).
                                            display: isLast ? {xs: "none", sm: "block"} : {xs: "none", sm: "block"},
                                        }}
                                    />
                                )}
                                {/* On mobile, only the current (last) crumb is shown. */}
                                <Box
                                    sx={{
                                        display: isLast ? "flex" : {xs: "none", sm: "flex"},
                                        minWidth: 0,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    <BreadcrumbLink crumb={crumb} />
                                </Box>
                            </React.Fragment>
                        )
                    })}
                </Box>
            </Box>

            {/* Tour + Docs */}
            <Box sx={{display: "flex", alignItems: "center", gap: 1, flexShrink: 0}}>
                {/* Local-dev only: preview the first-visit stepper on a
                    populated account (deliberately untranslated). */}
                {isLocalDev() && onDevFirstVisit && (
                    <Button
                        onClick={onDevFirstVisit}
                        aria-label={"first visit preview"}
                        startIcon={<ScienceRounded sx={{fontSize: 16}} />}
                        // Dev orange (dashboard `palette.dev`) — flags this as a
                        // local-development-only control.
                        sx={{
                            ...topBtnSx,
                            color: tk.color.dev,
                            borderColor: alpha(tk.color.dev, 0.45),
                            "&:hover": {backgroundColor: alpha(tk.color.dev, 0.08), borderColor: tk.color.dev},
                        }}
                    >
                        <Box component={"span"} sx={{display: {xs: "none", sm: "inline"}}}>First visit</Box>
                    </Button>
                )}
                {onStartTour && (
                    <Button
                        onClick={onStartTour}
                        aria-label={t("tour")}
                        startIcon={<AutoAwesomeRounded sx={{fontSize: 16}} />}
                        sx={topBtnSx}
                    >
                        <Box component={"span"} sx={{display: {xs: "none", sm: "inline"}}}>{t("tour")}</Box>
                    </Button>
                )}
                {/* Knowledge base (2026-07 tour rework) — replaces the old Docs
                    button in the same slot; the tour's last step points here.
                    Disabled until the knowledge base content goes live (the
                    docLink prop chain stays wired for the flip). */}
                <Button
                    disabled
                    aria-label={t("knowledge_base")}
                    data-step-target={"knowledge-base"}
                    startIcon={<MenuBookRounded sx={{fontSize: 16}} />}
                    sx={topBtnSx}
                >
                    <Box component={"span"} sx={{display: {xs: "none", sm: "inline"}}}>{t("knowledge_base")}</Box>
                </Button>
            </Box>
        </Box>
    )
}

// Shared top-bar button style. On mobile the label collapses (icon-only) so the
// start-icon margin is removed to keep the button square.
const topBtnSx = {
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 500,
    color: tk.color.textSecondary,
    border: `1px solid ${tk.color.border}`,
    borderRadius: tk.radius.sm,
    px: {xs: 1, sm: 1.5},
    py: 0.75,
    minWidth: 0,
    backgroundColor: tk.color.surface,
    "&:hover": {backgroundColor: tk.color.bg, borderColor: tk.color.borderStrong},
    "& .MuiButton-startIcon": {mr: {xs: 0, sm: 1}},
} as const
